import React from 'react';
import { Ridge } from 'shared/components/trodo-kit/Ridge';
import { Summit } from 'shared/components/trodo-kit/Summit';
import { Wind } from 'shared/components/trodo-kit/Wind';
import 'shared/components/trodo-kit/trodo-design-kit.css';
import { QUOTES } from './authTestimonials';

/* ============================================================
 *  The auth pages' ground: trodo.ai's hero range along the bottom
 *  (light: pale ridges and a low sun; dark: the night range with
 *  snow), wind specks drifting over it. Renders absolute inset-0
 *  behind the logo and the form.
 * ============================================================ */

function useDarkTheme() {
  const read = () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const [dark, setDark] = React.useState(read);
  React.useEffect(() => {
    const obs = new MutationObserver(() => setDark(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

const SUN = {
  dark: { cx: 800, cy: 342, r: 88, glow: 0 },
  light: { cx: 800, cy: 356, r: 84, glow: 0 },
}

export default function AuthBrandPanel() {
  const dark = useDarkTheme();
  const sun = dark ? SUN.dark : SUN.light;
  return (
    <div
      aria-hidden
      className="absolute inset-0 z-0 overflow-hidden select-none pointer-events-none"
      style={{
        background: dark ? '#141312' : '#f4f4f2',
      }}
    >
      {/* The sun's glow belongs to the panel, not the range: nothing clips it, so it fades out on its own and the scene is one image. */}
      <div
        className="absolute"
        style={{
          left: '50%', bottom: dark ? '-6%' : '-2%', width: 'min(60vw, 820px)', aspectRatio: '1 / 1',
          transform: 'translateX(-50%)',
          background: dark
            ? 'radial-gradient(circle, rgb(var(--trodo-primary-rgb) / .30) 0%, rgb(var(--trodo-primary-rgb) / .13) 36%, transparent 66%)'
            : 'radial-gradient(circle, rgb(var(--trodo-primary-rgb) / .30) 0%, rgb(var(--trodo-primary-rgb) / .14) 36%, transparent 68%)',
        }}
      />
      {/* The range keeps its canvas aspect (height follows width), so the SVG never crops a peak; on tall screens a minimum height scales it up and trims the sides instead. The sun sits in canvas units, a small part of it above the ridge at the centre. */}
      <div
        className={`${dark ? 'auth-summit' : 'td-ridge'} absolute inset-x-0 bottom-0`}
        style={{ aspectRatio: dark ? '1600 / 600' : '1600 / 560', minHeight: dark ? 300 : 280, overflow: 'visible' }}
      >
        {dark ? <Summit sun={sun} /> : <Ridge sun={sun} />}
        <Wind />
        {dark && <style>{'.auth-summit .sm { position: absolute; inset: 0; height: 100%; }'}</style>}
      </div>
    </div>
  );
}

/* ============================================================
 *  What teams say about Trodo: the quotes on trodo.ai/customers,
 *  one at a time, beside the form on wide screens.
 * ============================================================ */

const HOLD_MS = 9000;

export function AuthTestimonial() {
  const [i, setI] = React.useState(0);
  const [shown, setShown] = React.useState(true);
  const reduceMotion = React.useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  React.useEffect(() => {
    const t = setInterval(() => {
      if (reduceMotion) { setI((n) => (n + 1) % QUOTES.length); return; }
      setShown(false);
      setTimeout(() => { setI((n) => (n + 1) % QUOTES.length); setShown(true); }, 450);
    }, HOLD_MS);
    return () => clearInterval(t);
  }, [reduceMotion]);

  const q = QUOTES[i];
  const initials = q.name.split(' ').map((p) => p[0]).join('').slice(0, 2);
  const [photoFailed, setPhotoFailed] = React.useState(false);
  React.useEffect(() => setPhotoFailed(false), [i]);

  return (
    <figure className="max-w-[460px]">
      <div
        style={{
          opacity: shown ? 1 : 0,
          transform: shown ? 'none' : 'translateY(6px)',
          transition: 'opacity 450ms ease, transform 450ms ease',
        }}
      >
        <blockquote
          className={`${q.quote.length > 150 ? 'text-[22px]' : 'text-[26px]'} leading-[1.32] tracking-[-0.01em]`}
          style={{ color: 'var(--text-primary)', fontWeight: 300 }}
        >
          “{q.quote}”
        </blockquote>
        <figcaption className="mt-7 flex items-center gap-3">
          {q.photo && !photoFailed ? (
            <img
              src={q.photo}
              alt=""
              onError={() => setPhotoFailed(true)}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[12px] font-semibold"
              style={{ background: 'rgb(var(--trodo-primary-rgb) / 0.14)', color: 'var(--trodo-primary)' }}
            >
              {initials}
            </span>
          )}
          <span className="flex flex-col">
            <span className="text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>{q.name}</span>
            <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>{q.role}</span>
          </span>
          {q.logo && (
            <span className="ml-auto flex shrink-0 items-center gap-2" role="img" aria-label={q.logo.label} style={{ color: 'var(--text-muted)' }}>
              <span
                aria-hidden
                style={{
                  height: q.logo.h,
                  width: q.logo.h * q.logo.aspect,
                  background: 'currentColor',
                  opacity: 0.8,
                  WebkitMask: `url(${q.logo.src}) center / contain no-repeat`,
                  mask: `url(${q.logo.src}) center / contain no-repeat`,
                }}
              />
              {q.logo.text && (
                <span aria-hidden style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.16em', lineHeight: 1, opacity: 0.8 }}>{q.logo.text}</span>
              )}
            </span>
          )}
        </figcaption>
      </div>
      <div className="mt-8 flex gap-1.5" role="tablist" aria-label="Customer quotes">
        {QUOTES.map((x, k) => (
          <button
            key={x.name}
            type="button"
            role="tab"
            aria-selected={k === i}
            aria-label={x.name}
            onClick={() => { setI(k); setShown(true); }}
            className="h-1 rounded-full transition-all"
            style={{
              width: k === i ? 22 : 8,
              background: k === i ? 'var(--trodo-primary)' : 'var(--border-default)',
            }}
          />
        ))}
      </div>
    </figure>
  );
}
